import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, Loader2, CreditCard, Calendar, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useBookDemo } from "@/context/BookDemoContext";
import { createBillingCheckout, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const INCLUDED = [
    "300 AI-minutter",
    "AI-opkald",
    "Automatisk kundeoprettelse",
    "Automatisk joboprettelse",
    "Kunde-/job-/medarbejdersystem",
    "AI-samtaledata",
    "Kundens eksisterende telefonnummer via SIP",
    "E-mailbeskeder",
    "Hele kernesystemet",
    "Løbende opdateringer",
    "Ingen binding",
];

const ADDONS = [
    { name: "100 ekstra AI-minutter", price: "129 kr.", note: "engangskøb" },
    { name: "500 ekstra AI-minutter", price: "549 kr.", note: "engangskøb" },
    { name: "MyServiceHelper-telefonnummer", price: "199 kr./md.", note: null },
    { name: "Ekstra afdeling / AI-flow", price: "299 kr./md.", note: null },
];

export default function PricingCards() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { open: openDemo } = useBookDemo();
    const [loading, setLoading] = useState(false);

    const startSubscription = async () => {
        const packageId = "myservicehelper";
        if (!user) {
            try { window.sessionStorage.setItem("msh_post_login_checkout", packageId); } catch { /* noop */ }
            navigate("/login", { state: { from: "/priser", checkoutPackage: packageId } });
            return;
        }
        setLoading(true);
        try {
            const data = await createBillingCheckout(packageId);
            if (data?.url) { window.location.href = data.url; }
            else { toast.error("Kunne ikke starte betaling. Prøv igen."); setLoading(false); }
        } catch (err) {
            toast.error(formatApiError(err) || "Betaling fejlede");
            setLoading(false);
        }
    };

    return (
        <div data-testid="pricing-cards" className="max-w-lg mx-auto">
            <div
                data-testid="pricing-card-myservicehelper"
                className="relative rounded-2xl p-[2px] bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 shadow-2xl shadow-blue-500/20"
            >
                <div className="bg-white rounded-[14px] p-6 sm:p-7 flex flex-col">
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs font-semibold shadow-md">
                        <Sparkles className="w-3.5 h-3.5" />
                        Ét samlet abonnement
                    </div>

                    <div className="mb-4 mt-1">
                        <span className="text-xs font-semibold uppercase tracking-wider bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">MyServiceHelper</span>
                        <div className="mt-3 flex items-baseline gap-2">
                            <span data-testid="plan-price" className="text-4xl sm:text-5xl font-bold text-slate-900">695</span>
                            <span className="text-slate-500 text-sm">kr./md. ekskl. moms</span>
                        </div>
                        {/* Pilot / founding */}
                        <div data-testid="pilot-note" className="mt-3 rounded-xl bg-emerald-50 border border-emerald-100 px-3.5 py-2.5 text-[13px] text-emerald-800">
                            <span className="font-semibold">Pilotpris:</span> 499 kr./md. de første 2 måneder, derefter automatisk 695 kr./md.
                            <span className="block text-emerald-700/90 mt-0.5">0 kr. i oprettelse for de første 5 virksomheder.</span>
                        </div>
                    </div>

                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Inkluderet</p>
                    <ul className="space-y-2">
                        {INCLUDED.map((f, i) => (
                            <li key={i} data-testid={`myservicehelper-feature-${i}`} className="flex items-start gap-2.5 text-[13px] text-slate-700">
                                <span className="mt-0.5 shrink-0 w-4 h-4 rounded-full bg-emerald-50 text-emerald-600 inline-flex items-center justify-center">
                                    <Check className="w-2.5 h-2.5" />
                                </span>
                                <span>{f}</span>
                            </li>
                        ))}
                    </ul>

                    <div className="mt-6 pt-5 border-t border-slate-100 space-y-2">
                        <Button
                            data-testid="myservicehelper-start-subscription-btn"
                            onClick={startSubscription}
                            disabled={loading}
                            className="w-full rounded-full h-11 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md"
                        >
                            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                            Start abonnement
                        </Button>
                        <Button
                            data-testid="myservicehelper-demo-btn"
                            onClick={() => openDemo()}
                            variant="outline"
                            className="w-full rounded-full h-11 border-slate-300 text-slate-900 hover:bg-slate-50"
                        >
                            <Calendar className="w-4 h-4 mr-2" />
                            Book gratis demo
                        </Button>
                    </div>
                </div>
            </div>

            {/* Tilkøb — vises kun */}
            <div className="mt-5 rounded-2xl bg-white border border-slate-200 p-5" data-testid="pricing-addons">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Tilkøb</p>
                <ul className="space-y-2.5">
                    {ADDONS.map((a, i) => (
                        <li key={i} data-testid={`addon-${i}`} className="flex items-center justify-between gap-3">
                            <span className="flex items-center gap-2 text-[13px] text-slate-700">
                                <span className="shrink-0 w-4 h-4 rounded-full bg-blue-50 text-blue-600 inline-flex items-center justify-center"><Plus className="w-2.5 h-2.5" /></span>
                                {a.name}
                            </span>
                            <span className="text-[13px] text-slate-900 font-medium whitespace-nowrap">
                                {a.price}{a.note && <span className="text-slate-400 font-normal"> · {a.note}</span>}
                            </span>
                        </li>
                    ))}
                </ul>
                <p className="text-[11px] text-slate-400 mt-3">Tilkøb aftales i forbindelse med opstart.</p>
            </div>
        </div>
    );
}
